import { Injectable } from '@angular/core';
import { LandingModelService } from './landingModel.service';

export interface NumericAnimationCallbacks {
  onUpdatePrice: (formattedValue: string) => void;
  onAnimatingChange: (isAnimating: boolean) => void;
}

@Injectable({providedIn: 'root'})
export class AnimationService {
  readonly defaultDuration = 1500; // ms
  readonly minFrameInterval = 40; // máximo 25fps para no actualizar tan seguido

  constructor(private landingModelService: LandingModelService) {}

  /**
   * Inicia una animación numérica desde startValue hasta endValue con easing cubic-out.
   * Actualiza el valor mediante callbacks para mantener el componente actualizado.
   * 
   * @param startValue - Valor inicial
   * @param endValue - Valor final
   * @param onUpdatePrice - Callback para actualizar el valor del componente
   * @param durationOpt - Duración de la animación en ms
   * @returns Promise que se resuelve cuando la animación termina
   */
  public startNumericAnimation(startValue: number, endValue: number, onUpdatePrice: (value: string) => void, durationOpt?: number): Promise<void> {
    if (!startValue || !endValue || endValue >= startValue || endValue <= 0) {
      return Promise.resolve();
    }
    
    return new Promise<void>((resolve) => {
      const locale = this.landingModelService.getLocale().replace('_', '-');
      const formatter = new Intl.NumberFormat(locale);

      const initialTime = performance.now();
      const duration = durationOpt || this.defaultDuration;
      let lastValueShown = startValue;
      let lastUpdateTime = initialTime;

      const step = (time: number) => {
        const { currentValue, dynamicStep, shouldStop } = this.calculateDynamicStep(initialTime, time, startValue, endValue, duration);

        const enoughTimePassed = (time - lastUpdateTime) >= this.minFrameInterval;
        const currentValueRounded = Math.round(currentValue / dynamicStep) * dynamicStep;
        const valueChangedEnough = Math.abs(currentValueRounded - lastValueShown) >= dynamicStep;

        if (enoughTimePassed && valueChangedEnough) {
          lastValueShown = currentValueRounded;
          lastUpdateTime = time;
          onUpdatePrice(formatter.format(currentValueRounded));
        }

        if (shouldStop) {
          resolve();
          return;
        }

        requestAnimationFrame(step);
      };
      
      requestAnimationFrame(step);
    });
  }

  /**
   * Calcula el paso dinámico para la animación con easing cubic-out.
   * El paso disminuye a medida que la animación progresa para crear un efecto suave.
   * 
   * @param initialTime - Tiempo de inicio
   * @param currentTime - Tiempo actual
   * @param startValue - Valor inicial
   * @param endValue - Valor final
   * @param duration - Duración de la animación en ms
   * @returns Objeto con el valor actual, el paso dinámico y si debe detenerse
   */
  private calculateDynamicStep(
    initialTime: number, currentTime: number,
    startValue: number, endValue: number,
    duration: number
  ): { currentValue: number; dynamicStep: number; shouldStop: boolean } {
    const elapsed = currentTime - initialTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedOutCubic = 1 - Math.pow(1 - progress, 3); // Empieza rápido y termina lento
    const currentValue = startValue + (endValue - startValue) * easedOutCubic;

    const remainingRatio = 1 - progress; // Va de 1 a 0
    const targetStepsRemaining = 8 + remainingRatio * 27; // Va de 35 a 8
    const dynamicStep = Math.max(1, Math.round(Math.abs(endValue - currentValue) / targetStepsRemaining));

    const shouldStop = progress >= 1 || currentValue <= endValue;

    return { currentValue, dynamicStep, shouldStop };
  }
}
